import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { VIDEO_MIME_TYPE_TO_EXTENSION, MAX_VIDEO_DURATION } from "@/constants/media";
import { IconFrame } from "@scripts/react/components/IconFrame";
import { QrUploadWidget } from "@/components/QrUploadWidget";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useQrProcessingEffect } from "@/store";
import InlineAlert from "@scripts/react/components/InlineAlert";
import { useTestMediaSupport } from "@/hooks/useTestMediaSupport";
import { useMobile } from "@/hooks/useMobile";
import { MIN_INFERENCES_SAMPLE } from "@/constants/inference";
import Button from "@scripts/react/components/Button";
import { RfModal } from "@scripts/react/components/RfModal/RfModal";
import { useMedia } from "@/contexts/MediaContext";
import { UploadLimitErrorAlert } from "@/components/flow/shared/UploadLimitErrorAlert";
import { UploadLimitErrorType } from "@/constants/media";
import { InlineTutorialCard } from "@/components/tutorial/TutorialCard";

const UploadSection = ({
    isMobile,
    isActiveAnimation,
    isDragging,
    fileDragError,
    fileInputError,
    showAllMethods,
    isSharePage,
    isUsePage,
    setIsQRModalOpen,
    uploadLimitError,
    clearUploadLimitError
}: {
    isMobile?: boolean;
    isActiveAnimation: boolean;
    isDragging: boolean;
    fileDragError: string | string[] | null;
    fileInputError: Error[] | null;
    showAllMethods: boolean;
    isSharePage?: boolean;
    isUsePage?: boolean;
    setIsQRModalOpen?: (isOpen: boolean) => void;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
}) => {
    const { track } = useAnalytics();

    return (
        <div className={twClassNames("fileUploadMethod justify-center")}>
            {!showAllMethods && (
                <UploadLimitErrorAlert
                    isOpen={!!uploadLimitError}
                    onClose={clearUploadLimitError || (() => {})}
                    errorType={uploadLimitError}
                    hideIcon={isSharePage}
                />
            )}
            <div className="fileUploadMethodContent">
                <IconFrame
                    icon={isActiveAnimation ? "fad fa-arrow-up" : "far fa-arrow-up"}
                    size="lg"
                    color={isActiveAnimation ? "purple" : "gray"}
                    isCircle
                    className={twClassNames(
                        "transition duration-200 ease-out",
                        isActiveAnimation && "scale-110"
                    )}
                />
                {isDragging ? (
                    <h2 className="text-brand">Drop your files here</h2>
                ) : (
                    <div className="flex flex-col gap-2">
                        <h2
                            className={twClassNames(
                                "sm:text-xl",
                                isActiveAnimation ? "text-brand" : "text-primary",
                                isSharePage && !isUsePage
                                    ? "whitespace-pre-line"
                                    : "whitespace-nowrap"
                            )}
                        >
                            {isSharePage || isUsePage
                                ? "Upload a file to try this model"
                                : showAllMethods
                                  ? "Upload images or videos"
                                  : `Upload a short video or ${MIN_INFERENCES_SAMPLE} images`}
                        </h2>
                        <div className="flex flex-col items-center gap-1 text-xs text-tertiary">
                            <p>Videos will be trimmed to {MAX_VIDEO_DURATION} seconds.</p>
                        </div>
                    </div>
                )}
            </div>

            {fileDragError && (
                <InlineAlert level="danger" type="primary" className="py-3 pr-3 text-left">
                    {fileDragError}
                </InlineAlert>
            )}
            {/* 
            {!showAllMethods && !isDragging && setIsQRModalOpen && (
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        track({ event: AnalyticsFlowEvent.QR_UPLOAD_BUTTON_CLICKED });
                        setIsQRModalOpen(true);
                    }}
                    size="xsmall"
                    type="ghost"
                    className="self-center font-normal"
                >
                     <i className="far fa-mobile" />
                    {isSharePage ? (
                        <p>Or, try mobile upload</p>
                    ) : (
                        <p>Want to upload from your phone?</p>
                    )}
                </Button>
            )} */}

            {fileInputError && (
                <InlineAlert level="danger" type="primary" className="py-3 pr-3">
                    <div className="text-destructive">
                        {fileInputError.map((error) => (
                            <p key={error.message}>{error.message}</p>
                        ))}
                    </div>
                    <p>Please try again with a different file.</p>
                </InlineAlert>
            )}
        </div>
    );
};

const Divider = ({ isActiveAnimation }: { isActiveAnimation: boolean }) => (
    <div className="fileUploadMethodContent">
        <div
            className={twClassNames(
                "h-full w-px",
                isActiveAnimation ? "bg-purple-200" : "bg-gray-200"
            )}
        />
        <p
            className={twClassNames(
                "font-mono text-xs",
                isActiveAnimation ? "text-purple-500" : "text-tertiary"
            )}
        >
            OR
        </p>
        <div
            className={twClassNames(
                "h-full w-px",
                isActiveAnimation ? "bg-purple-200" : "bg-gray-200"
            )}
        />
    </div>
);

const QRUploadSection = ({
    isActiveAnimation,
    qrSize
}: {
    isActiveAnimation: boolean;
    qrSize: number;
}) => (
    <div className="fileUploadMethod">
        <div className="fileUploadMethodContent">
            <IconFrame
                icon={isActiveAnimation ? "fad fa-mobile-screen" : "far fa-mobile"}
                size="lg"
                color={isActiveAnimation ? "purple" : "gray"}
                isCircle
                className={twClassNames(
                    "transition duration-200 ease-out",
                    isActiveAnimation && "scale-110"
                )}
            />
            <h2
                className={twClassNames(
                    "text-lg font-semibold sm:text-xl",
                    isActiveAnimation ? "text-brand" : "text-primary"
                )}
            >
                Capture data with your phone or tablet
            </h2>
        </div>

        <QrUploadWidget size={qrSize} />

        <div className="flex flex-col items-center gap-1 text-xs text-tertiary">
            Scan the QR code with your device to get started.
        </div>
    </div>
);

const QRUploadModal = ({
    isOpen,
    onClose,
    qrSize
}: {
    isOpen: boolean;
    onClose: () => void;
    qrSize: number;
}) => (
    <RfModal isOpen={isOpen} onClose={onClose}>
        <RfModal.Overlay />
        <RfModal.Panel className="fileUploadContainer max-w-md">
            <RfModal.CloseButton />
            <QRUploadSection isActiveAnimation={false} qrSize={qrSize} />
            <Button type="secondary" size="medium" onClick={onClose} className="mt-4">
                No thanks, I want to upload from my computer
            </Button>
        </RfModal.Panel>
    </RfModal>
);

function ShowAllMethodsFileInput({
    isActiveAnimation,
    isDragging,
    fileDragError,
    fileInputError,
    qrSize,
    isMobile,
    uploadLimitError,
    clearUploadLimitError,
    isSharePage
}: {
    isActiveAnimation: boolean;
    isDragging: boolean;
    fileDragError: string | string[] | null;
    fileInputError: Error[] | null;
    qrSize: number;
    isMobile: boolean;
    uploadLimitError: UploadLimitErrorType | null;
    clearUploadLimitError: () => void;
    isSharePage?: boolean;
}) {
    const shouldHideOthers = isDragging || isMobile;
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState<number | null>(null);

    useEffect(() => {
        if (containerRef.current && !shouldHideOthers && containerHeight === null) {
            setContainerHeight(containerRef.current.offsetHeight);
        }
    }, [shouldHideOthers, containerHeight]);

    return (
        <div
            ref={containerRef}
            className="flex h-full w-full flex-col"
            style={{ height: containerHeight ? `${containerHeight}px` : "auto" }}
        >
            {uploadLimitError && (
                <div className="flex justify-center pt-6">
                    <UploadLimitErrorAlert
                        isOpen={true}
                        onClose={clearUploadLimitError}
                        errorType={uploadLimitError}
                        hideIcon={isSharePage}
                    />
                </div>
            )}
            <div
                className="fileUploadContainer grid flex-1"
                style={{ gridTemplateColumns: shouldHideOthers ? "1fr" : "1fr auto 1fr" }}
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key={isDragging ? "show-all-methods-dragging" : "show-all-methods-not-dragging"}
                    transition={{ duration: 0.15, ease: "easeInOut" }}
                    className={shouldHideOthers ? "col-span-full" : ""}
                >
                    <UploadSection
                        isMobile={isMobile}
                        isActiveAnimation={isActiveAnimation}
                        isDragging={isDragging}
                        fileDragError={fileDragError}
                        fileInputError={fileInputError}
                        showAllMethods={true}
                    />
                </motion.div>

                {!shouldHideOthers && (
                    <>
                        <Divider isActiveAnimation={isActiveAnimation} />
                        <QRUploadSection isActiveAnimation={isActiveAnimation} qrSize={qrSize} />
                    </>
                )}
            </div>
        </div>
    );
}

function ShowOneMethodFileInput({
    isActiveAnimation,
    isDragging,
    fileDragError,
    fileInputError,
    qrSize,
    isSharePage,
    isUsePage,
    isQRModalOpen,
    setIsQRModalOpen,
    uploadLimitError,
    clearUploadLimitError
}: {
    isActiveAnimation: boolean;
    isDragging: boolean;
    fileDragError: string | string[] | null;
    fileInputError: Error[] | null;
    qrSize: number;
    isSharePage?: boolean;
    isUsePage?: boolean;
    isQRModalOpen: boolean;
    setIsQRModalOpen: (isOpen: boolean) => void;
    uploadLimitError: UploadLimitErrorType | null;
    clearUploadLimitError: () => void;
}) {
    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={isDragging ? "show-one-method-dragging" : "show-one-method-not-dragging"}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                className={twClassNames("fileUploadContainer hidden h-full sm:flex sm:pt-12")}
            >
                <UploadSection
                    isActiveAnimation={isActiveAnimation}
                    isDragging={isDragging}
                    fileDragError={fileDragError}
                    fileInputError={fileInputError}
                    showAllMethods={false}
                    isSharePage={isSharePage}
                    isUsePage={isUsePage}
                    setIsQRModalOpen={setIsQRModalOpen}
                    uploadLimitError={uploadLimitError}
                    clearUploadLimitError={clearUploadLimitError}
                />
            </motion.div>

            <QRUploadModal
                isOpen={isQRModalOpen}
                onClose={() => setIsQRModalOpen(false)}
                qrSize={qrSize}
            />
        </>
    );
}

interface FileInputAreaProps {
    addMediaItems: (files: File[]) => void;
    erroredMediaItems?: any[];
    qrSize?: number;
    showAllMethods?: boolean;
    isSharePage?: boolean;
    isUsePage?: boolean;
    externalIsHovered?: boolean;
    hasEverUploadedFiles?: boolean;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
}

export const FileInputArea = ({
    addMediaItems,
    erroredMediaItems = [],
    qrSize = 64,
    showAllMethods = false,
    isSharePage = false,
    isUsePage = false,
    externalIsHovered = false,
    hasEverUploadedFiles = false,
    uploadLimitError: uploadLimitErrorProp,
    clearUploadLimitError: clearUploadLimitErrorProp
}: FileInputAreaProps) => {
    const { track } = useAnalytics();
    const {
        uploadLimitError: uploadLimitErrorFromContext,
        clearUploadLimitError: clearUploadLimitErrorFromContext
    } = useMedia();

    // Use props if provided, otherwise fall back to context
    const uploadLimitError = uploadLimitErrorProp ?? uploadLimitErrorFromContext;
    const clearUploadLimitError = clearUploadLimitErrorProp ?? clearUploadLimitErrorFromContext;
    const [isDragging, setIsDragging] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [fileInputError, setFileInputError] = useState<Error[] | null>(null);
    const [fileDragError, setFileDragError] = useState<string | string[] | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const uploadAreaRef = useRef<HTMLDivElement>(null);
    const { result: testedSupportedMimeTypes } = useTestMediaSupport();
    const isMobile = useMobile();
    const disableDueToMobile = isMobile && !showAllMethods;
    const [isQRModalOpen, setIsQRModalOpen] = useState(false);
    const isActiveAnimation =
        (isHovered || externalIsHovered || isDragging) && !disableDueToMobile && !isQRModalOpen;

    // Since we now have backend video re-encoding, accept ALL video formats
    // Use browser-supported images + ALL video formats
    const acceptedMimeTypes = useMemo(() => {
        const allVideoMimeTypes = Object.keys(VIDEO_MIME_TYPE_TO_EXTENSION);
        const supportedImageMimeTypes = testedSupportedMimeTypes?.imageMimeTypesSupported || [];
        return [...supportedImageMimeTypes, ...allVideoMimeTypes];
    }, [testedSupportedMimeTypes]);

    const acceptedExtensions = useMemo(() => {
        const allVideoExtensions = Object.values(VIDEO_MIME_TYPE_TO_EXTENSION).map(
            (info) => info.ext
        );
        const supportedImageExtensions = testedSupportedMimeTypes?.imageExtensionsSupported || [];
        // Use Set to deduplicate extensions (e.g., .avi appears multiple times)
        const uniqueExtensions = new Set([...supportedImageExtensions, ...allVideoExtensions]);
        return Array.from(uniqueExtensions);
    }, [testedSupportedMimeTypes]);

    // Memoized drag state reset function
    const resetDragState = useCallback(() => {
        setIsDragging(false);
        setIsHovered(false);
    }, []);

    const resetErrorState = useCallback(() => {
        setFileInputError(null);
        setFileDragError(null);
    }, []);

    // Class name helper
    const getUploadClasses = useCallback(() => {
        return twClassNames(
            // Base styles
            "flex w-full overflow-hidden text-center transition-all duration-100 ease-out group rounded-xl border-2 border-dashed border-gray-300",
            // Don't stretch height when showing inline tutorial card on mobile
            !disableDueToMobile && "flex-1 h-full",
            showAllMethods || hasEverUploadedFiles
                ? "items-stretch justify-between"
                : "items-center justify-center",
            isUsePage || isSharePage ? "bg-gray-50" : "bg-white",
            isActiveAnimation
                ? "border-brand bg-tint cursor-pointer"
                : (showAllMethods || hasEverUploadedFiles) && "bg-gray-50",
            showAllMethods || hasEverUploadedFiles
                ? isMobile
                    ? "h-full"
                    : "rounded-xl h-full"
                : isUsePage
                  ? "max-w-none ring-4 ring-inset ring-white"
                  : "max-w-screen-lg ring-4 ring-inset ring-white"
        );
    }, [
        showAllMethods,
        isActiveAnimation,
        isMobile,
        hasEverUploadedFiles,
        disableDueToMobile,
        isUsePage,
        isSharePage
    ]);

    const checkDraggingValidFiles = useCallback(
        (e: DragEvent) => {
            if (e.dataTransfer && e.dataTransfer.items) {
                const items = Array.from(e.dataTransfer.items);
                const invalidItems = items.filter((item) => {
                    // Accept if MIME type matches or if MIME type is empty (browser doesn't recognize it)
                    // Empty MIME types are common for formats like .mkv, .avi, etc.
                    return item.type && !acceptedMimeTypes.includes(item.type);
                });

                if (invalidItems.length) {
                    const noValid = items.length === invalidItems.length;
                    setFileDragError([
                        `${noValid ? "All files" : "Some files"} you are trying to add are not supported and will be ignored.\nWe recommend trying one of the following formats: ${acceptedExtensions.join(", ")}`
                    ]);
                    if (noValid) {
                        e.dataTransfer.dropEffect = "none"; // prevent dropping
                    }
                } else {
                    setFileDragError(null);
                    e.dataTransfer.dropEffect = "copy";
                }
            }
        },
        [acceptedMimeTypes, acceptedExtensions]
    );

    const handleGlobalDrop = useCallback(
        (e: DragEvent) => {
            // Safari doesn't expose files/items during dragenter/over for security reasons
            // we check on drop specifically for safari to show an error
            checkDraggingValidFiles(e);
        },
        [checkDraggingValidFiles]
    );

    const handleGlobalDragOver = useCallback(
        (e: DragEvent) => {
            if (uploadAreaRef.current) {
                const rect = uploadAreaRef.current.getBoundingClientRect();
                const isInside =
                    e.clientX >= rect.left &&
                    e.clientX <= rect.right &&
                    e.clientY >= rect.top &&
                    e.clientY <= rect.bottom;

                if (isInside) {
                    // Set dragging state when inside upload area
                    setIsDragging(true);
                    // check if what we are dragging is valid or not
                    checkDraggingValidFiles(e);
                } else if (isDragging || isHovered) {
                    // Reset state when outside upload area
                    resetDragState();
                    resetErrorState();
                }
            }
        },
        [checkDraggingValidFiles, isDragging, isHovered, resetDragState, resetErrorState]
    );

    // Memoized visibility change handler
    const handleVisibilityChange = useCallback(() => {
        if (document.hidden && (isDragging || isHovered)) {
            resetDragState();
            resetErrorState();
        }
    }, [isDragging, isHovered, resetDragState, resetErrorState]);

    // Add effect to handle drag end when switching screens/apps
    useEffect(() => {
        // Only listen to events we actually need
        document.addEventListener("dragover", handleGlobalDragOver);
        document.addEventListener("drop", handleGlobalDrop);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("dragover", handleGlobalDragOver);
            document.removeEventListener("drop", handleGlobalDrop);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [handleGlobalDragOver, handleGlobalDrop, handleVisibilityChange]);

    // Memoized QR processing callbacks
    const handleQrFileProcessed = useCallback(
        (file: File) => addMediaItems([file]),
        [addMediaItems]
    );

    const handleQrAnalytics = useCallback(
        (event: string, data: any) =>
            track({
                event: AnalyticsFlowEvent.FILE_UPLOADER_CLICKED,
                data: { ...data, source: "qr_code" }
            }),
        [track]
    );

    // QR Upload processing - handles QR files before Carousel is rendered
    useQrProcessingEffect({
        enabled: true, // always enabled for upload flow
        onFileProcessed: handleQrFileProcessed,
        onAnalytics: handleQrAnalytics
    });

    // Handle file input errors
    useEffect(() => {
        if (erroredMediaItems.length) {
            setFileInputError(erroredMediaItems.map((item) => item.error));
        } else {
            setFileInputError(null);
        }
    }, [erroredMediaItems]);

    const handleFileUploadClick = useCallback(() => {
        // Disable upload on mobile screens
        if (disableDueToMobile) return;

        track({ event: AnalyticsFlowEvent.FILE_UPLOADER_CLICKED });
        fileInputRef.current?.click();
    }, [track, disableDueToMobile]);

    const handleCameraClick = useCallback(() => {
        cameraInputRef.current?.click();
        track({ event: AnalyticsFlowEvent.CAMERA_INPUT_CLICKED });
    }, [track]);

    const handleFileInputClick = useCallback(
        (e: React.MouseEvent<HTMLInputElement>) => {
            e.stopPropagation(); // prevent parent drag and drop from triggering twice
            track({ event: AnalyticsFlowEvent.FILE_INPUT_CLICKED });
        },
        [track]
    );

    const handleDragEvent = useCallback((e: React.DragEvent) => {
        // All drag state management handled by global listener
        e.preventDefault();
    }, []);

    const handleDrop = useCallback(
        async (e: React.DragEvent) => {
            e.preventDefault();
            if (disableDueToMobile) return;

            // Accept all supported files (browser-supported images + ALL video formats)
            // Also accept files with empty MIME types (browser doesn't recognize format)
            const files = Array.from(e.dataTransfer.files).filter((file) => {
                return !file.type || acceptedMimeTypes.includes(file.type);
            });

            if (files && files.length > 0) {
                addMediaItems(files);
            }

            resetDragState();
        },
        [addMediaItems, acceptedMimeTypes, resetDragState, disableDueToMobile]
    );

    const handleFileSelected = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (disableDueToMobile) return;

            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
                addMediaItems(files);
            }
            // Reset the input value so the same file can be selected again
            e.target.value = "";
            resetErrorState();
        },
        [addMediaItems, resetErrorState, disableDueToMobile]
    );

    return (
        <div
            ref={uploadAreaRef}
            className={getUploadClasses()}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onDrop={handleDrop}
            onDragEnter={handleDragEvent}
            onDragOver={handleDragEvent}
            onDragLeave={handleDragEvent}
            onClick={handleFileUploadClick}
            data-testid="file-upload-area"
        >
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptedMimeTypes.join(",")}
                multiple
                onChange={handleFileSelected}
                onClick={handleFileInputClick}
                className="hidden"
                data-testid="file-input"
            />
            <input
                ref={cameraInputRef}
                type="file"
                accept={acceptedMimeTypes.join(",")}
                capture="environment"
                onChange={handleFileSelected}
                className="hidden"
                data-video-quality="high"
                data-testid="camera-input"
            />

            {disableDueToMobile ? (
                <InlineTutorialCard />
            ) : showAllMethods ? (
                <ShowAllMethodsFileInput
                    isActiveAnimation={isActiveAnimation}
                    isDragging={isDragging}
                    fileDragError={fileDragError}
                    fileInputError={fileInputError}
                    qrSize={qrSize}
                    isMobile={isMobile}
                    uploadLimitError={uploadLimitError}
                    clearUploadLimitError={clearUploadLimitError}
                    isSharePage={isSharePage}
                />
            ) : (
                <ShowOneMethodFileInput
                    isActiveAnimation={isActiveAnimation}
                    isDragging={isDragging}
                    fileDragError={fileDragError}
                    fileInputError={fileInputError}
                    qrSize={qrSize}
                    isSharePage={isSharePage}
                    isUsePage={isUsePage}
                    isQRModalOpen={isQRModalOpen}
                    setIsQRModalOpen={setIsQRModalOpen}
                    uploadLimitError={uploadLimitError}
                    clearUploadLimitError={clearUploadLimitError}
                />
            )}
        </div>
    );
};
